import { Injectable, inject } from '@angular/core';
import { UrlFormatter } from 'shared';
import { Environment, ENVIRONMENT_TOKEN } from 'account-hybrid/core/environment.service';

/**
 * WS5 #10 Stage A (BroadcastModal port) — native port of the AngularJS
 * `shareOAuth` provider (account-v1/src/components/curation/shareOAuth.service.js,
 * the `OAuthWindow` class). Opens the social-share OAuth popup and points it at
 * the v1 OAuth endpoints. The v1 provider was configured with
 * `shareOAuthProvider.setV1BaseUrl(environment.v1BaseUrl)` (app.module.ts) — the
 * same Environment object backs ENVIRONMENT_TOKEN, so the URLs are value-identical.
 * URL building reuses the shared `UrlFormatter.format(path, model, true)` static
 * (the byte-identical port of the v1 `urlFormatter`).
 *
 * `log()` is a no-op: v1 forwarded to `AppLog` — an in-memory FixedQueue only
 * readable via the manual `window.log` dump (no console/network output), a
 * diagnostic surface that dies with AngularJS in the Stage B boot-stop.
 *
 * The popup Window handle is static, mirroring v1's `OAuthWindow.windowInstance`
 * (one shared popup across instances).
 */
@Injectable({ providedIn: 'root' })
export class ShareOAuthService {
  private static windowInstance: Window | null = null;

  private static readonly windowSizeByNetwork: Record<string, { width: number; height: number }> = {
    '1': { width: 640, height: 730 },
    '2': { width: 800, height: 450 },
    '3': { width: 600, height: 600 },
  };

  private readonly environment = inject(ENVIRONMENT_TOKEN) as Environment;

  isSupported(networkType: any): boolean {
    return networkType == 1 || networkType == 2 || networkType == 3;
  }

  getWindowFeatures(networkType: any): string {
    if (!this.isSupported(networkType)) throw 'Network does not require oauth';
    const size = ShareOAuthService.windowSizeByNetwork[networkType];
    const left = document.documentElement.clientWidth / 2 - size.width / 2;
    const top = document.documentElement.clientHeight / 2 - size.height / 2;

    const features =
      'titlebar=0,toolbar=0,location=0,directories=0,status=0,menubar=0,scrollbars=yes,resize=yes,modal=yes,width=' +
      size.width + ',height=' + size.height + ',top=' + top + ',left=' + left;
    return (this.environment as any).debugOAuth ? '' : features;
  }

  tryOpenWindow(networkType: any): void {
    if (!this.isSupported(networkType)) return;
    const features = this.getWindowFeatures(networkType);
    ShareOAuthService.windowInstance = window.open('', '_blank', features);
  }

  trySetLocation(networkType: any, model: any): void {
    if (!this.isSupported(networkType) || !ShareOAuthService.windowInstance) return;
    const v1BaseUrl = (this.environment as any).v1BaseUrl ?? '';
    if (networkType == 1) {
      if (!!model.invitationUrl) {
        (ShareOAuthService.windowInstance as any).location =
          v1BaseUrl + UrlFormatter.format('oauth/facebook/invitation-share', model, true);
      } else if (!!model.websiteShareUrl) {
        (ShareOAuthService.windowInstance as any).location =
          v1BaseUrl + UrlFormatter.format('oauth/facebook/website-share', model, true);
      } else {
        (ShareOAuthService.windowInstance as any).location =
          v1BaseUrl + UrlFormatter.format('oauth/facebook/curation-share', model, true);
      }
    } else {
      (ShareOAuthService.windowInstance as any).location =
        v1BaseUrl + UrlFormatter.format('socialnetwork/sharing/oauth/authorize', model, true);
    }
  }

  close(): void {
    ShareOAuthService.windowInstance?.close();
  }

  log(_action: string, _args: any): void {
    // v1: AppLog.info → in-memory diagnostic queue only. Intentionally not replicated.
  }
}
